import React, { useCallback, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { fetchDevicePictures } from '../../services/apiService.js';
import { isValidCoordinate } from '../../utils/reportFormatters';
import { useReportData } from '../../context/ReportDataContext';
import { WalteroCard, WalteroTypography } from '../waltero';

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Use default Leaflet icon
const defaultIcon = new L.Icon.Default();

// Component to adjust map bounds when devices change
const MapBoundsAdjuster = ({ devices }) => {
  const map = useMap();
  const prevDeviceIdsRef = useRef([]);

  useEffect(() => {
    const currentDeviceIds = devices.map(d => d.deviceId).sort().join(',');
    const prevDeviceIds = prevDeviceIdsRef.current.join(',');

    if (currentDeviceIds !== prevDeviceIds) {
      const deviceLocations = devices.map(device => [device.latitude, device.longitude]);
      
      console.log(`MapBoundsAdjuster: Processing ${deviceLocations.length} device locations.`);

      if (deviceLocations.length > 0) {
        const TOO_MANY_POINTS_THRESHOLD = 5000;
        const DEFAULT_MAX_ZOOM_FOR_FITBOUNDS = 12;

        if (deviceLocations.length > TOO_MANY_POINTS_THRESHOLD) {
          console.warn(`MapBoundsAdjuster: Exceeded threshold of ${TOO_MANY_POINTS_THRESHOLD} devices. Setting default map view.`);
          map.setView([51.505, -0.09], 5);
        } else {
          const bounds = L.latLngBounds(deviceLocations);
          if (bounds.isValid()) {
            console.log("MapBoundsAdjuster: Fitting bounds with maxZoom:", DEFAULT_MAX_ZOOM_FOR_FITBOUNDS);
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: DEFAULT_MAX_ZOOM_FOR_FITBOUNDS });
          } else {
            console.warn("MapBoundsAdjuster: Calculated bounds are invalid. Setting default view.");
            map.setView([51.505, -0.09], 5);
          }
        }
      } else {
        console.log("MapBoundsAdjuster: No valid device locations to fit bounds. Setting default view.");
        map.setView([51.505, -0.09], 5);
      }
      
      prevDeviceIdsRef.current = devices.map(d => d.deviceId).sort();
    }
  }, [devices, map]);

  return null;
};

const DevicesMap = ({ devices }) => {
  console.log('🗺️ DevicesMap received devices:', devices);
  
  const { popupImageCache, updatePopupImageCache } = useReportData();
  
  // Filter devices with valid coordinates
  const devicesForMap = React.useMemo(() => {
    return devices.filter(device => isValidCoordinate(device.latitude, device.longitude));
  }, [devices]);
  
  console.log(`🗺️ Found ${devicesForMap.length} devices with coordinates out of ${devices.length} total`);

  // Marker interaction handler
  const handleMarkerInteraction = useCallback(async (deviceId) => {
    // Check if already loaded or loading
    const currentCache = popupImageCache[deviceId];
    if (currentCache && (currentCache.images.length > 0 || currentCache.isLoading)) {
      console.log(`📸 Pictures already loaded for device: ${deviceId}`);
      return;
    }
    
    console.log(`📸 Loading pictures for device: ${deviceId}`);
    updatePopupImageCache(deviceId, {
      images: [], 
      currentIndex: 0, 
      lastEvaluatedKey: null, 
      hasMore: false, 
      error: null, 
      isLoading: true
    });
    
    try {
      console.log(`🌐 Making API call to fetch pictures for device: ${deviceId}`);
      const picturesData = await fetchDevicePictures(deviceId);
      console.log(`✅ Pictures loaded for device ${deviceId}:`, picturesData);
      
      const pictures = picturesData.pictures || [];
      console.log(`📸 Extracted pictures array:`, pictures);
      
      updatePopupImageCache(deviceId, {
        images: pictures, 
        hasMore: false, 
        isLoading: false, 
        currentIndex: 0, 
        error: pictures.length === 0 ? 'No images found.' : null,
        lastEvaluatedKey: null
      });
    } catch (error) {
      console.error(`❌ Error loading pictures for device ${deviceId}:`, error);
      updatePopupImageCache(deviceId, {
        images: [], 
        error: error instanceof Error ? error.message : 'Failed to load images', 
        isLoading: false,
        currentIndex: 0,
        hasMore: false,
        lastEvaluatedKey: null
      });
    }
  }, [popupImageCache, updatePopupImageCache]);

  // Navigate to next image
  const handleNextImage = useCallback((deviceId) => {
    const currentEntry = popupImageCache[deviceId];
    if (!currentEntry || currentEntry.isLoading) return;
    if (currentEntry.currentIndex < currentEntry.images.length - 1) {
      updatePopupImageCache(deviceId, {
        ...currentEntry,
        currentIndex: currentEntry.currentIndex + 1
      });
    }
  }, [popupImageCache, updatePopupImageCache]);

  // Navigate to previous image
  const handlePreviousImage = useCallback((deviceId) => {
    const currentEntry = popupImageCache[deviceId];
    if (!currentEntry || currentEntry.isLoading) return;
    if (currentEntry.currentIndex > 0) {
      updatePopupImageCache(deviceId, {
        ...currentEntry,
        currentIndex: currentEntry.currentIndex - 1
      });
    }
  }, [popupImageCache, updatePopupImageCache]);

  const defaultPosition = [51.505, -0.09];

  if (devicesForMap.length === 0) {
    return (
      <div className="map-container">
        <WalteroCard variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <WalteroTypography variant="h6" color="primary" gutterBottom>
            No devices with location data found
          </WalteroTypography>
          <WalteroTypography variant="body2" color="secondary">
            Devices need latitude and longitude coordinates to appear on the map.
          </WalteroTypography>
        </WalteroCard>
      </div>
    );
  }

  return (
    <div className="map-container">
      <div className="map-header">
        <h3>Device Locations</h3>
        <div className="device-count">
          Displaying {devicesForMap.length} devices on the map.
        </div>
      </div>
      <div className="leaflet-map">
        <MapContainer
          center={defaultPosition}
          zoom={5}
          style={{ height: '500px', width: '100%' }}
          preferCanvas={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MapBoundsAdjuster devices={devicesForMap} />

          {devicesForMap.map(device => (
            <Marker
              key={device.deviceId}
              position={[device.latitude, device.longitude]}
              icon={defaultIcon}
              eventHandlers={{
                popupopen: () => handleMarkerInteraction(device.deviceId),
              }}
            >
              <Popup
                key={`popup-${device.deviceId}`}
                autoPan={false}
              >
                {(() => {
                  const cacheEntry = popupImageCache[device.deviceId];
                  if (cacheEntry) {
                    const currentImage = cacheEntry.images.length > 0 && cacheEntry.currentIndex < cacheEntry.images.length 
                                       ? cacheEntry.images[cacheEntry.currentIndex] 
                                       : null;
                    const isLoadingImage = cacheEntry.isLoading;
                    const imageError = cacheEntry.error;
                    const totalImagesInCache = cacheEntry.images.length;
                    const hasNext = cacheEntry.currentIndex < totalImagesInCache - 1;
                    const hasPrevious = cacheEntry.currentIndex > 0;
                    
                    return (
                      <div style={{ minWidth: '220px' }}>
                        <b>Serial:</b> {device.serialNumber}<br />
                        <b>Name:</b> {device.deviceName || 'N/A'}<br />
                        <b>Meter:</b> {device.meterName || 'N/A'}<br />
                        <hr />
                        
                        <div style={{ minHeight: '150px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          {isLoadingImage && <p>Loading image...</p>}
                          {imageError && !isLoadingImage && <p style={{ color: 'red', fontSize: '0.9em' }}>Error: {imageError}</p>}
                          {!isLoadingImage && !imageError && !currentImage && <p>No image available.</p>}
                          {currentImage && !isLoadingImage && !imageError && (
                            <div style={{ textAlign: 'center' }}>
                              <img
                                src={currentImage.url}
                                alt={`Sensor ${device.serialNumber} - Image ${cacheEntry.currentIndex + 1}`}
                                style={{ maxWidth: '200px', maxHeight: '150px', height: 'auto', borderRadius: '4px' }}
                                onError={(e) => {
                                  console.error(`❌ Failed to load image for device ${device.deviceId}:`, currentImage.url);
                                  console.error(`❌ Full picture object:`, currentImage);
                                }}
                                onLoad={() => {
                                  console.log(`✅ Image loaded successfully for device ${device.deviceId}:`, currentImage.url);
                                }}
                              />
                              <p style={{ fontSize: '0.8em', margin: '5px 0 0 0' }}>
                                {new Date(currentImage.lastModified).toLocaleString()}
                              </p>
                            </div>
                          )}
                        </div>

                        <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <button 
                            onClick={() => handlePreviousImage(device.deviceId)} 
                            disabled={!hasPrevious || isLoadingImage} 
                            style={{ fontSize: '1.2em' }}
                          >
                            &larr;
                          </button>
                          <span style={{ fontSize: '0.9em' }}>
                            {totalImagesInCache > 0 ? `${cacheEntry.currentIndex + 1} / ${totalImagesInCache}` : '0 / 0'}
                          </span>
                          <button 
                            onClick={() => handleNextImage(device.deviceId)} 
                            disabled={!hasNext || isLoadingImage} 
                            style={{ fontSize: '1.2em' }}
                          >
                            &rarr;
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return <p>Loading popup...</p>;
                })()}
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
};

export default DevicesMap;